import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Badge, Card, ErrorState, Loading, PageHeader } from '@/components/ui';

/**
 * What this account is allowed, and what it is using.
 *
 * A client could see none of this. The numbers govern whether their next bot
 * can be created and whether their next call is answered, and the only way to
 * learn one was to hit it — a call refused for capacity looks, from the
 * portal, exactly like a call that was never made.
 */

interface Allowance {
  label: string;
  used: number;
  limit: number | null;
  remaining: number | null;
  at_limit: boolean;
  note: string;
}

interface Limits {
  status: string;
  trial_ends_at: string | null;
  allowances: Allowance[];
  hint: string;
}

function Bar({ used, limit }: { used: number; limit: number | null }) {
  if (!limit) return null;
  const pct = Math.min(100, Math.round((used / limit) * 100));
  return (
    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-ink-100 dark:bg-ink-800">
      <div
        className={
          'h-full rounded-full ' +
          (pct >= 100
            ? 'bg-red-500'
            : pct >= 80
              ? 'bg-amber-500'
              : 'bg-brand-500')
        }
        style={{ width: `${Math.max(pct, 2)}%` }}
      />
    </div>
  );
}

export default function Allowances() {
  const limits = useQuery({
    queryKey: ['account-limits'],
    queryFn: () => api.get<Limits>('/account/limits'),
    // Simultaneous calls is a live number.
    refetchInterval: 10_000,
  });

  if (limits.isPending) return <Loading />;
  if (limits.error)
    return <ErrorState error={limits.error} retry={() => limits.refetch()} />;

  const { allowances, hint, status, trial_ends_at: trialEnds } = limits.data;

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title="Plan & limits"
        description="What your account is allowed, and what it is using right now."
        actions={
          status === 'TRIAL' ? (
            <Badge tone="amber">
              Trial
              {trialEnds
                ? ` · ends ${new Date(trialEnds).toLocaleDateString()}`
                : ''}
            </Badge>
          ) : (
            <Badge tone="green">{status.toLowerCase()}</Badge>
          )
        }
      />

      <div className="grid gap-3 sm:grid-cols-2">
        {allowances.map((row) => (
          <Card key={row.label} padded>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium text-ink-800 dark:text-ink-100">
                {row.label}
              </span>
              {row.at_limit ? <Badge tone="red">at the limit</Badge> : null}
            </div>

            <div className="mt-1 flex items-baseline gap-1.5">
              <span className="text-2xl font-semibold tabular-nums text-ink-900 dark:text-ink-50">
                {row.used}
              </span>
              <span className="text-sm text-ink-500">
                {/* No limit set is "no limit", not a limit of nought. Zero has
                    always meant "not configured" on these columns. */}
                {row.limit === null ? 'in use · no limit set' : `of ${row.limit}`}
              </span>
            </div>

            <Bar used={row.used} limit={row.limit} />

            <p className="mt-2 text-xs leading-relaxed text-ink-500 dark:text-ink-400">
              {row.note}
            </p>
          </Card>
        ))}
      </div>

      <p className="mt-4 text-xs text-ink-500 dark:text-ink-400">{hint}</p>
    </div>
  );
}
